/* QuadrantRenderer.js */

"use strict";
/* jshint expr: true */

const TWEEN = require("@tweenjs/tween.js");
const _ = require("lodash");
const QuadrantMenuComponent = require("./quadrantMenu/QuadrantMenuComponent");
const QuadrantContentComponent = require("./quadrantContent/QuadrantContentComponent");
let counter = 0;

class QuadrantRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("quadrant"); //TODO Setup the root Element
        this._rootElem.component = this._model;
        this._rootElem.classList.add(this._model.name);
        this.rootElement.setAttribute("tabindex", -1);
        this._state = {
            opened: false,
        };
        this._openCloseDuration = 400;
        this._menu = new QuadrantMenuComponent({state: this._model._state});
        this._contentComponent = new QuadrantContentComponent({
            quadrant: this._model.name,
            state: this._model._state,
        });
        // DOM representation of the drag slider bar for this quadrant
        this._sliderBar = document.createElement("slider-bar");

        if (this._model._name === "right" || this._model._name === "left") {
            // Constant representing the default min width (percentage) applied to the left/right quadrants when the 'opened' class is applied to them
            this._DEFAULT_MIN_SIZE = 28.65;
            // Constant representing the smallest min width (percentage) that can be applied to the left/right quadrants
            this._BASE_MIN_SIZE = 10;
            this._sizeUnit = "%";
        }
        if (this._model._name === "bottom") {
            this._DEFAULT_MIN_SIZE = 400;
            this._BASE_MIN_SIZE = 100;
            this._sizeUnit = "px";
        }

        this.rootElement.appendChild(this._contentComponent.renderer.rootElement);
        this.rootElement.appendChild(this._sliderBar);
        this.rootElement.appendChild(this._menu.renderer.rootElement);
        this._registerListeners();
        //this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/
    _animate(time) {
        if (this._model.isTransitioning) {
            TWEEN.update(time);
            requestAnimationFrame(this._animate);
        }
    }

    _animateOnce(time) {
        if (counter <= 30) {
            counter++;
            TWEEN.update(time);
            requestAnimationFrame(this._animateOnce);
        }
    }

    _shutdown() {
        this._unregisterListeners();
        return Promise.resolve()
            .then(() => {
                if (this._contentComponent.openedPart) {
                    return this.model.closeLMRBPart(this._contentComponent.openedPart);
                }
            })
            .then(() => {
                this._menu.shutdown();
                this._menu = null;
                this._contentComponent.shutdown();
                this._contentComponent = null;
                this._sliderBar.remove();
                this._sliderBar = null;
                this._rootElem.component = null;
                this._rootElem.remove();
                this._rootElem = null;
                this._model = null;
            });
    }

    _registerListeners() {
        //request to activate docklet
        this._menu.on("activatePart", (partInfo) => {
            this._model.requestPartActivation(partInfo);
        });
        this._animate = this._animate.bind(this);
        this._animateOnce = this._animateOnce.bind(this);
        this._sliderMouseDownHandler = this._sliderMouseDownHandler.bind(this);
        this._windowMouseUpHandler = this._windowMouseUpHandler.bind(this);
        this._windowMouseMoveHandler = this._windowMouseMoveHandler.bind(this);
        this._menuStateChanged = this._menuStateChanged.bind(this);

        this._sliderBar.addEventListener("mousedown", this._sliderMouseDownHandler);
    }

    _unregisterListeners() {
        this._sliderBar.removeEventListener("mousedown", this._sliderMouseDownHandler);
    }

    _menuStateChanged() {
        let state = this._model._state;
        state.optionsOpened ? this._setOpenOptionsClass() : this._removeOpenOptionsClass();
        state.menuVisible ? this._setMenuVisibleClass() : this._removeMenuVisibleClass();
        state.pinned ? this._setMenuPinnedClass() : this._removeMenuPinnedClass();
    }

    _setOpenOptionsClass() {
        this.rootElement.classList.add("options-open-" + this._model.name);
    }

    _removeOpenOptionsClass() {
        this.rootElement.classList.remove("options-open-" + this._model.name);
    }

    _setMenuVisibleClass() {
        this.rootElement.classList.add("options-hovered-" + this._model.name);
    }

    _removeMenuVisibleClass() {
        this.rootElement.classList.remove("options-hovered-" + this._model.name);
    }

    _setMenuPinnedClass() {
        this.rootElement.classList.add("pinned");
    }

    _removeMenuPinnedClass() {
        this.rootElement.classList.remove("pinned");
    }

    /**
     * Event handler for the "mousedown" event that is fired when a user clicks on the slider-bar (drag slider) DOM
     * element inside of the Quadrant.
     * @param {Event} event - The event object
     */
    _sliderMouseDownHandler(event) {
        if (this._model._name === "left") {
            this._listOffsetSize = this.rootElement.querySelector("active-docklets").offsetWidth;
            this._cursorOffset = event.pageX - this.rootElement.offsetWidth;
            this._cursorOffset = this.rootElement.classList.contains("pinned")
                ? this._cursorOffset - this._listOffsetSize
                : this._cursorOffset;
        } else if (this._model._name === "right") {
            this._listOffsetSize = this.rootElement.querySelector("active-docklets").offsetWidth;
            this._cursorOffset = this._sliderBar.offsetWidth - event.offsetX;
        } else if (this._model._name === "bottom") {
            this._listOffsetSize = this.rootElement.querySelector("active-docklets").offsetHeight;
            this._cursorOffset = this._sliderBar.offsetHeight - event.offsetY;
            this._cursorOffset = this.rootElement.classList.contains("pinned")
                ? this._cursorOffset + this._listOffsetSize
                : this._cursorOffset;
        }
        this.rootElement.classList.add("dragging");
        this.rootElement.classList.add("dragging-sliders");
        window.addEventListener("mousemove", this._windowMouseMoveHandler);
        window.addEventListener("mouseup", this._windowMouseUpHandler);
    }

    /**
     * Event handler for the "mouseup" event (bound to the window object) that is fired when a user releases
     * the mouse. This is a temporary binding made when the user clicks on the slider-bar (drag slider) DOM
     * element inside of the Quadrant.
     * @param {Event} event - The event object
     */
    _windowMouseUpHandler() {
        this._cleanupSliderInteraction();
    }

    _cleanupSliderInteraction() {
        this._cleanupPositioningVars();
        this.rootElement.classList.remove("dragging");
        this.rootElement.classList.remove("dragging-sliders");
        window.removeEventListener("mousemove", this._windowMouseMoveHandler);
        window.removeEventListener("mouseup", this._windowMouseUpHandler);
    }

    _cleanupPositioningVars() {
        this._cursorOffset = 0;
        this._listOffsetSize = 0;
        this._maxWidthReached = false;
        this._maxWidthAdjustmentAmount = 0;
    }

    /**
     * Event handler for the "mousemove" event (bound to the window object) that is fired when a user moves
     * the mouse. This is a temporary binding made when the user clicks on the slider-bar (drag slider) DOM
     * element inside of the Quadrant.
     * @param {Event} event - The event object
     */
    _windowMouseMoveHandler(event) {
        if (this._model._name === "left") {
            let newWidth;
            let interfacePadding =
                document.body.offsetWidth - this.rootElement.parentNode.offsetWidth;
            let percentageFromEdge = event.pageX / document.body.offsetWidth;
            let adjustmentAmount = interfacePadding * percentageFromEdge;
            if (this.rootElement.classList.contains("pinned")) {
                newWidth =
                    ((event.pageX - this._cursorOffset - this._listOffsetSize + adjustmentAmount) /
                        document.body.offsetWidth) *
                    100;
            } else {
                newWidth =
                    ((event.pageX - this._cursorOffset + adjustmentAmount) /
                        document.body.offsetWidth) *
                    100;
            }
            this._size = newWidth;
            this._maxQuadrantWidthCheck(adjustmentAmount);
        } else if (this._model._name === "right") {
            let newWidth;
            let interfacePadding =
                document.body.offsetWidth - this.rootElement.parentNode.offsetWidth;
            let percentageFromEdge =
                (document.body.offsetWidth - event.pageX) / document.body.offsetWidth;
            let adjustmentAmount = interfacePadding * percentageFromEdge;
            if (this.rootElement.classList.contains("pinned")) {
                newWidth =
                    ((document.body.offsetWidth -
                        event.pageX -
                        this._cursorOffset -
                        this._listOffsetSize +
                        adjustmentAmount) /
                        document.body.offsetWidth) *
                    100;
            } else {
                newWidth =
                    ((document.body.offsetWidth -
                        event.pageX -
                        this._cursorOffset +
                        adjustmentAmount) /
                        document.body.offsetWidth) *
                    100;
            }
            this._size = newWidth;
            this._maxQuadrantWidthCheck(adjustmentAmount);
        } else if (this._model._name === "bottom") {
            let newHeight = document.body.offsetHeight - event.pageY - this._cursorOffset;
            this._size = newHeight;
        }
    }

    _maxQuadrantWidthCheck(adjustmentAmount) {
        if (this.rootElement.parentNode.scrollWidth > this.rootElement.parentNode.offsetWidth) {
            if (!this._maxWidthReached) {
                this._maxWidthAdjustmentAmount = adjustmentAmount;
            }
            this._maxWidthReached = true;
            let sizeDifference =
                this.rootElement.parentNode.scrollWidth - this.rootElement.parentNode.offsetWidth;
            this._size =
                ((this._pixelSize - sizeDifference + this._maxWidthAdjustmentAmount) /
                    document.body.offsetWidth) *
                100;
        }
    }

    set _size(val) {
        this._openSize = val > this._BASE_MIN_SIZE ? val : this._BASE_MIN_SIZE;
        // console.log("_size");
        // console.log(this._openSize);
        // console.log(this._size);
        this._elementSize = this._openSize;
        this._model.openedSize = this._openSize;
        if (this._openSize < this._DEFAULT_MIN_SIZE) {
            this._elementMinSize = this._openSize;
        }
    }

    get _size() {
        if (this._model.name === "bottom") {
            return this.rootElement.offsetHeight;
        } else {
            return (this.rootElement.offsetWidth / document.body.offsetWidth) * 100;

            //return this.rootElement.offsetWidth
        }
    }

    get _pixelSize() {
        return this.rootElement.offsetWidth;
    }

    get _sizePropertyName() {
        if (this._model.name === "bottom") {
            return "height";
        } else {
            return "width";
        }
    }

    get _minSizePropertyName() {
        return "min-" + this._sizePropertyName;
    }

    set _elementSize(size) {
        // console.log(this._sizePropertyName);
        // console.log(size + this._sizeUnit);
        this.rootElement.style[this._sizePropertyName] = size + this._sizeUnit;
    }

    get _elementSize() {
        return this.rootElement.style[this._sizePropertyName];
    }

    set _elementMinSize(size) {
        this.rootElement.style[this._minSizePropertyName] = size + this._sizeUnit;
    }

    get _elementMinSize() {
        return this.rootElement.style[this._minSizePropertyName];
    }

    _openQuadrantContent(animate) {
        return new Promise((resolve, reject) => {
            if (!this._state.opened) {
                if (animate) {
                    try {
                        this._model.isTransitioning = true;
                        let tweenObj = {
                            size: 0,
                        };
                        let newSize = _.clone(this._model.openedSize) || this._DEFAULT_MIN_SIZE;
                        new TWEEN.Tween(tweenObj)
                            .to({size: newSize}, this._openCloseDuration)
                            .easing(TWEEN.Easing.Quintic.InOut)
                            .onUpdate(() => {
                                this._elementSize = tweenObj.size;
                            })
                            .onComplete(() => {
                                this.rootElement.classList.add("opened");
                                this._size = tweenObj.size;
                                this._state.opened = true;
                                this._model.isTransitioning = false;
                                resolve();
                            })
                            .start();

                        requestAnimationFrame(this._animate);
                    } catch (e) {
                        reject(e);
                    }
                } else {
                    this.rootElement.classList.add("opened");
                    this._size = _.clone(this._model.openedSize) || this._DEFAULT_MIN_SIZE;
                    this._state.opened = true;
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    /**
     * Closes the Quadrant and resolves the promise when the "transitionEnd" event is fired.
     * @returns {Promise} - The close promise
     */
    _closeQuadrantContent(animate) {
        return Promise.resolve().then(() => {
            return new Promise((resolve, reject) => {
                if (this._state.opened) {
                    if (animate) {
                        try {
                            this._model.isTransitioning = true;
                            let tweenObj = {
                                size: _.clone(this._model.openedSize),
                            };
                            this._elementMinSize = 0;

                            new TWEEN.Tween(tweenObj)
                                .to(
                                    {
                                        size: 0,
                                    },
                                    this._openCloseDuration
                                )
                                .easing(TWEEN.Easing.Quintic.InOut)
                                .onUpdate(() => {
                                    this._elementSize = tweenObj.size;
                                })
                                .onComplete(() => {
                                    this.rootElement.classList.remove("opened");
                                    this._state.opened = false;
                                    this._model.isTransitioning = false;
                                    resolve();
                                })
                                .start();

                            requestAnimationFrame(this._animate);
                        } catch (e) {
                            reject(e);
                        }
                    } else {
                        this.rootElement.removeAttribute("style");
                        this.rootElement.classList.remove("opened");
                        this._state.opened = false;
                        resolve();
                    }
                } else {
                    resolve();
                }
            });
        });
    }

    _openPartInQuadrant() {
        return Promise.resolve()
            .then(() => {
                return this._model.openLMRBPart(this._model.openedPartId);
            })
            .then((part) => {
                if (this._model.fullScreen) {
                    return Promise.resolve()
                        .then(() => {
                            return this._openQuadrantContent(false);
                        })
                        .then(() => {
                            //TODO make the id something that is stored in the component's state
                            return this._contentComponent.showPart(
                                part,
                                this._model.openedPartId,
                                true
                            );
                        });
                } else {
                    return this._contentComponent
                        .whenSwappingIsFinished()
                        .then(() => {
                            return this._contentComponent.showPart(
                                part,
                                this._model.openedPartId,
                                this._model.fullScreen
                            );
                        })
                        .then(() => {
                            return this._openQuadrantContent(true);
                        });
                }
            })
            .then(() => {
                this._model.quadrantSizeChanged();
                return Promise.resolve();
            });
    }

    _closeQuadrantAndPart() {
        return Promise.resolve()
            .then(() => {
                if (this._contentComponent.fullScreen) {
                    //close without animating the quadrant
                    return this._closeQuadrantContent(false);
                } else {
                    //close with animation
                    return this._closeQuadrantContent(true);
                }
            })
            .then(() => {
                this._model.fullScreen = false;
                return this._contentComponent.removePart(false);
            })
            .then((part) => {
                return this._model.closeLMRBPart(part);
            })
            .then(() => {
                this._model.quadrantSizeChanged();
                return Promise.resolve();
            });
    }

    _renderFullScreenState() {
        if (this._model.fullScreen !== this._contentComponent.fullScreen) {
            this._contentComponent.fullScreen = this._model.fullScreen;
            return this._contentComponent.renderer.render();
        } else {
            return Promise.resolve();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     */
    render() {
        // console.log(this._model._name + " render!");
        this._menuStateChanged();

        return this._menu.renderer
            .render()
            .then(() => {
                if (this._model.openedPartId) {
                    if (this._model.openedPartId !== this._contentComponent.renderedPartId) {
                        //the opened part has been set to something other than what is currently open in the content component
                        return this._openPartInQuadrant();
                    } else {
                        return this._renderFullScreenState();
                    }
                } else {
                    if (this._state.opened) {
                        return this._closeQuadrantAndPart();
                    } else {
                        // console.log("quadrant state is updated");
                        return Promise.resolve();
                    }
                }
            })
            .catch((e) => {
                this.model.log.warn(e);
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = QuadrantRenderer;
